import React, { useEffect, useState } from "react";
import {
  getGreeting,
  getMotivationalMessage,
  getTimeOfDay,
} from "../utils/motivationalMessages";

// Progress-based encouragement (moved here from the progress bar so the
// page has one motivational spot instead of two).
const getProgressMessage = (percentage) => {
  if (percentage === 100) return "🎉 Challenge complete — you're amazing!";
  if (percentage >= 80) return "🔥 Almost there. Keep pushing!";
  if (percentage >= 60) return "💪 Great progress — you're doing awesome.";
  if (percentage >= 40) return "✨ Keep going. You're on the right track.";
  if (percentage >= 20) return "🌟 Good start. Stay consistent.";
  return "🚀 Let's do this. Every day counts.";
};

/**
 * Time-aware greeting + motivational line.
 * - The greeting ("Good Morning, {name}", "Hello, Night Owl {name}", ...) and
 *   the motivational message are picked on every load.
 * - While the app stays open, a 60s timer watches the time-of-day. When the
 *   bucket changes (e.g. morning -> afternoon) the greeting and message refresh
 *   automatically, so the header always matches the real time.
 * - A second, progress-based line reflects how far along the challenge is.
 */
const Greeting = ({ userName, completed = 0, total = 0 }) => {
  const [greeting, setGreeting] = useState(() => getGreeting(userName));
  const [message, setMessage] = useState(() => getMotivationalMessage());

  useEffect(() => {
    // Re-pick whenever the user name changes (e.g. after login).
    setGreeting(getGreeting(userName));
    setMessage(getMotivationalMessage());

    let lastBucket = getTimeOfDay();
    const interval = setInterval(() => {
      const bucket = getTimeOfDay();
      if (bucket !== lastBucket) {
        lastBucket = bucket;
        setGreeting(getGreeting(userName));
        setMessage(getMotivationalMessage());
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [userName]);

  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="text-center mb-6 sm:mb-8">
      <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-transparent bg-clip-text brand-gradient mb-2">
        {greeting}
      </h2>
      <p className="text-sm sm:text-base md:text-lg text-cyan-300 max-w-2xl mx-auto italic">
        {message}
      </p>
      {total > 0 && (
        <span className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-gray-800/70 border border-gray-600 text-xs sm:text-sm text-yellow-300 font-semibold">
          {getProgressMessage(percentage)}
        </span>
      )}
    </div>
  );
};

export default Greeting;
